import {
  Condition,
  COUNTRIES,
  CreditRating,
  CrmType,
  CurrencyType,
  DateFormat,
  FinanceType,
  FuelEfficiencyUnit,
  IncentivesProvider,
  InventoryType,
  Language,
  PriceRuleSource,
  PrintingType,
  SourceType,
  TradeInType,
  UserRole,
} from '@upstart/harmony/lib/constants';
import { InStoreControlPanelSteps } from '@upstart/harmony/lib/flow-utils';
import { cloneDeep, set } from 'lodash';
import zipcodeToTimezone from 'zipcode-to-timezone';
import {
  CreditPricingLeasePricing,
  CreditPricingPurchasePricing,
  CreditTier,
} from '@upstart/harmony/lib/legos/creditPricingType';
import { Days, Dealer, AprConfig } from '../../types/dealerTypes';
import { User } from '../../types/userTypes';
import { BRANDS, DAY_LIST } from '../../utils/constants';
import { FeeRow, FeeTable } from './types';

/**
 * Returns the list of likely dealer brands based on the dealer name
 */
export const getDefaultBrands = (dealer: Partial<Dealer>) => {
  const dealerName = dealer.name?.toLowerCase();
  if (!dealerName) return [];
  const brands = [];

  BRANDS.forEach((brand) => {
    if (dealerName.includes(brand.toLowerCase())) {
      brands.push(brand);
    }
  });

  if (dealerName.includes('cdjr')) {
    brands.push('Chrysler', 'Dodge', 'Jeep', 'Ram');
  }

  return brands;
};

const getDefaultFeeRows = (financeType: FinanceType): Partial<FeeRow>[] => [
  {
    name: 'Doc Fee',
    value: undefined,
    taxable: true,
    capCost: false,
    includeInFeeStack: true,
  },
  ...(financeType === FinanceType.LEASE
    ? [
        {
          name: 'Acquisition Fee',
          value: undefined,
          taxable: true,
          capCost: false,
          includeInFeeStack: true,
        },
      ]
    : []),
];

/**
 * Returns the list of default fees for each finance type
 */
export const getDefaultFees = (): FeeTable =>
  Object.values(FinanceType).reduce(
    (prev: Partial<FeeTable>, financeType) => ({
      ...prev,
      [financeType]: getDefaultFeeRows(financeType),
    }),
    {},
  ) as FeeTable;

// default price source
export const DEFAULT_PRICE_SOURCE = PriceRuleSource.FEED_PRICE;
export const DEFAULT_CREDIT_TIERS: CreditTier[] = [
  { name: 'Tier1', key: 'tier1', value: 751 },
  { name: 'Tier2', key: 'tier2', value: 651 },
  { name: 'Tier3', key: 'tier3', value: 601 },
  { name: 'Tier4', key: 'tier4', value: 500 },
];
export const DEFAULT_PURCHASE_PRICING: CreditPricingPurchasePricing[][] = [
  [
    { apr: 3.99, markup: 0 },
    { apr: 3.99, markup: 0 },
    { apr: 4.99, markup: 0 },
  ],
  [
    { apr: 6.99, markup: 0 },
    { apr: 6.99, markup: 0 },
    { apr: 7.99, markup: 0 },
  ],
  [
    { apr: 10.99, markup: 0 },
    { apr: 10.99, markup: 0 },
    { apr: 11.99, markup: 0 },
  ],
  [
    { apr: 18.99, markup: 0 },
    { apr: 18.99, markup: 0 },
    { apr: 19.99, markup: 0 },
  ],
];

export const DEFAULT_LEASE_PRICING: CreditPricingLeasePricing[][] = [
  [
    { moneyFactor: 50, onePayLeaseMoneyFactor: 0 },
    { moneyFactor: 50, onePayLeaseMoneyFactor: 0 },
    { moneyFactor: 50, onePayLeaseMoneyFactor: 0 },
  ],
  [
    { moneyFactor: 75, onePayLeaseMoneyFactor: 0 },
    { moneyFactor: 75, onePayLeaseMoneyFactor: 0 },
    { moneyFactor: 75, onePayLeaseMoneyFactor: 0 },
  ],
  [
    { moneyFactor: 100, onePayLeaseMoneyFactor: 0 },
    { moneyFactor: 100, onePayLeaseMoneyFactor: 0 },
    { moneyFactor: 100, onePayLeaseMoneyFactor: 0 },
  ],
  [
    { moneyFactor: 125, onePayLeaseMoneyFactor: 0 },
    { moneyFactor: 125, onePayLeaseMoneyFactor: 0 },
    { moneyFactor: 125, onePayLeaseMoneyFactor: 0 },
  ],
];
// default apr config
export const DEFAULT_APR_CONFIG: AprConfig = {
  creditTiers: {
    poor: { low: 500, high: 599 },
    average: { low: 600, high: 649 },
    good: { low: 650, high: 739 },
    excellent: { low: 740, high: 850 },
  },
  purchase: {
    terms: { term1: 48, term2: 60, term3: 72 },
    credit: {
      poor: { apr: { term1: 18.99, term2: 18.99, term3: 19.99 } },
      average: { apr: { term1: 10.99, term2: 10.99, term3: 11.99 } },
      good: { apr: { term1: 6.99, term2: 6.99, term3: 7.99 } },
      excellent: { apr: { term1: 3.99, term2: 3.99, term3: 4.99 } },
    },
  },
  lease: {
    mileages: { mileage1: 10000, mileage2: 12000, mileage3: 15000 },
    terms: {
      term1: {
        term: 36,
        credit: {
          poor: {
            moneyFactor: { mileage1: 125, mileage2: 125, mileage3: 125 },
          },
          average: {
            moneyFactor: { mileage1: 100, mileage2: 100, mileage3: 100 },
          },
          good: { moneyFactor: { mileage1: 75, mileage2: 75, mileage3: 75 } },
          excellent: {
            moneyFactor: { mileage1: 50, mileage2: 50, mileage3: 50 },
          },
        },
      },
    },
    credit: {
      poor: { moneyFactor: { mileage1: 125, mileage2: 125, mileage3: 125 } },
      average: { moneyFactor: { mileage1: 100, mileage2: 100, mileage3: 100 } },
      good: { moneyFactor: { mileage1: 75, mileage2: 75, mileage3: 75 } },
      excellent: {
        moneyFactor: { mileage1: 50, mileage2: 50, mileage3: 50 },
      },
    },
  },
  options: {
    useManufacturerApr: {
      purchase: true,
      [CreditRating.EXCELLENT]: true,
      [CreditRating.GOOD]: true,
      [CreditRating.AVERAGE]: false,
      [CreditRating.POOR]: false,
    },
    useManufacturerRebate: {
      purchase: true,
      lease: true,
      cash: true,
    },
  },
};

/**
 * Returns a dealer with defaults suitable for a new launchpad dealer
 */
export const addNewDealerDefaults = <T extends Partial<Dealer>>(
  dealer: T,
  dealerContactEmail?: string,
): T => ({
  ...dealer,

  crmDefaults: {
    type: SourceType.INTERNET,
  },

  testDriveSpeed: 20,
  testDriveDuration: 5,

  printingType: PrintingType.AIRPRINT,

  stepFlowConfig: {
    instore: [
      InStoreControlPanelSteps.CUSTOMER_INFO,
      InStoreControlPanelSteps.VEHICLE_SELECTION,
      InStoreControlPanelSteps.DRIVER_LICENSE,
      InStoreControlPanelSteps.TEST_DRIVE,
      InStoreControlPanelSteps.TRADE_IN,
      InStoreControlPanelSteps.CAR_PAYMENT_BUILDER,
      InStoreControlPanelSteps.CREDIT_APPLICATION,
      InStoreControlPanelSteps.DOCUMENT_SCAN,
      InStoreControlPanelSteps.FORM_LIBRARY,
    ],
  },

  wizard: {
    splashPage: {
      alternateHeaders: true,
    },
    paymentBuilder: {
      showPaymentSummary: true,
    },
    leadForm: {
      piiFlow: 1,
    },
    enableProdigyWeb: { enabled: true },
  },

  desking: {
    enabled: true,
    defaultPencil: false,
    managerDesking: true,
    onlineDesking: true,
    showAdditionalCustomerIncentives: true,
    showAllAdditionalCustomerIncentives: true,
    showSeparateLowInterestHighRebateOffers: true,
    ignoreMRM: false,
    enableOnePayLease: false,
    addInceptionFeesToTotalAtStart: true,
    inceptionFeesTotalAtStart: {
      taxes: { enabled: false },
      fees: { enabled: false },
      firstMonth: { enabled: false },
      customerToggle: { enabled: false },
      enabledByDefault: true,
    },
    applyRebatesToDueAtSigning: false,
    useTradeInAllowanceForTax: {
      enabled: true,
      [Condition.NEW]: true,
      [Condition.USED]: true,
    },
  },

  aprConfig: {
    New: DEFAULT_APR_CONFIG,
    Used: DEFAULT_APR_CONFIG,
  },
  inStoreAprConfig: {
    New: DEFAULT_APR_CONFIG,
    Used: DEFAULT_APR_CONFIG,
  },

  tabletFormsConfig: {
    customerInfo: {
      email: {
        required: true,
      },
      phone: {
        required: true,
      },
    },
    vehicleSelection: {
      daysUntilAgedUnit: {
        New: 60,
        Used: 90,
      },
    },
    fiveLine: {
      enabled: true,
    },
  },

  featureConfig: {
    thirdPartyLeads: {
      forwardEmailToCustomer: false,
      forwardTextToCustomer: false,
    },
    globalPricing: {
      usePriceOverrides: {
        'In-store': true,
        Online: true,
        Forwarded: true,
      },
      forwardedPriceOverrideExpiration: 1,
    },
    incentives: {
      provider: IncentivesProvider.AIS,
    },
    inventoryImages: {
      chromeDataIsPrimaryImageSource: false,
    },
    downPayment: {
      default: 0.1,
    },
  },

  localizationConfig: {
    defaultLanguage: Language.EN_US,
    supportedLanguages: [Language.EN_US, Language.ES_MX],
    dateFormat: DateFormat.MM_DD_YYYY,
    fuelEfficiencyUnit: FuelEfficiencyUnit.MPG,
    currency: CurrencyType.USD,
  },

  ownershipOptionsRules: {
    New: {
      instore: {
        purchase: { enabled: true },
        lease: { enabled: true },
        cash: { enabled: true },
      },
      online: {
        purchase: { enabled: true },
        lease: { enabled: true },
        cash: { enabled: true },
      },
    },
    Used: {
      instore: {
        purchase: { enabled: true },
        cash: { enabled: true },
      },
      online: {
        purchase: { enabled: true },
        cash: { enabled: true },
      },
    },
  },

  launchpad: {
    ...dealer.launchpad,
    email: dealerContactEmail,
  },
});

const STATES_WITH_USE_TRADEIN_ALLOWANCE_FOR_TAX = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'LA',
  'ME',
  'MD',
  'MA',
  'MN',
  'MS',
  'MO',
  'NE',
  'NV',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'WA',
  'WV',
  'WI',
  'WY',
];

/**
 * Returns a dealer with the defaults based on contact info set
 */
export const addContactInfoDefaults = <T extends Partial<Dealer>>(
  dealer: T,
): T => ({
  ...dealer,
  // set a country and timezone based on the address
  country: COUNTRIES.US,
  timezone: dealer.zip ? zipcodeToTimezone.lookup(dealer.zip) : null,
  desking: {
    ...dealer.desking,
    useTradeInAllowanceForTax: {
      enabled:
        !!dealer.state &&
        STATES_WITH_USE_TRADEIN_ALLOWANCE_FOR_TAX.includes(dealer.state),
    },
  },
});

/**
 * Returns a dealer with defaults based on the set users
 */
export const addUsersDefaults = <T extends Partial<Dealer>>(
  dealer: T,
  users: User[],
): T => ({
  ...dealer,
  managers: users
    .filter((u) => u.roles?.includes(UserRole.DESKING_MANAGER))
    .map((user) => ({
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      email: user.email || undefined,
      phone: user.phone || undefined,
      shouldSendTurnover: true,
      shouldSendLead: true,
    })),
});

const defaultAvailabilityHours = {
  ...(DAY_LIST.reduce(
    (prev: Partial<Days>, day) => ({
      ...prev,
      [day]: {
        enabled: !day.toLowerCase().startsWith('s'),
        start: 10,
        end: 19,
      },
    }),
    {},
  ) as Days),
};

const PICKUP_DEFAULTS = {
  enabled: true,
  appointmentWindow: '30',
  startsAfter: '12 hours',
  endsAfter: '5 days',
  days: defaultAvailabilityHours,
};

const DELIVERY_DEFAULTS = {
  enabled: false,
  startsAfter: '12 hours',
  endsAfter: '5 days',
  radius: 50,
  days: defaultAvailabilityHours,
};

/**
 * Returns a dealer with defaults for pickup and delivery
 */
export const addDefaultPickupDelivery = <T extends Partial<Dealer>>(
  dealer: T,
): T => ({
  ...dealer,
  wizard: {
    ...dealer.wizard,
    pickup: {
      ...PICKUP_DEFAULTS,
      ...dealer.wizard?.pickup,
    },
    delivery: {
      ...DELIVERY_DEFAULTS,
      ...dealer.wizard?.delivery,
    },
  },
});

/**
 * Returns a dealer with defaults for Business hours
 */
export const addDefaultLending = <T extends Partial<Dealer>>(dealer: T): T => ({
  ...dealer,
  lending: {
    businessHours: defaultAvailabilityHours,
    ...dealer.lending,
  },
});

/**
 * Returns a dealer with defaults based on the set crmType
 */
export const addCRMDefaults = <T extends Partial<Dealer>>(dealer: T): T => {
  // get the default lead source id for 'Prodigy' based on the crm type
  let sourceId;
  switch (dealer.crmType) {
    case CrmType.ELEAD:
      sourceId = '1072';
      break;
    case CrmType.VINSOLUTIONS:
      sourceId = '207511';
      break;
    case CrmType.DEALERSOCKET:
      sourceId = '3665';
      break;
    case CrmType.REYNOLDS:
      sourceId = '101043';
      break;
    default:
  }
  return {
    ...dealer,
    crmDefaults: { ...dealer.crmDefaults, sourceId },
    crmConfig: {
      ...dealer.crmConfig,
      pushToDms: dealer.crmType === CrmType.DEALERSOCKET,
    },
  };
};

/**
 * Returns a dealer with the tradeInManagerType defaulted if it's not specified
 */
export const addDefaultTradeInManager = <T extends Partial<Dealer>>(
  dealer: T,
): T => {
  return {
    ...dealer,
    tradeInManagerType:
      dealer.tradeInManagerType ||
      // if vauto is set as the inventory provider, default them for the tradein manager
      (dealer.launchpad?.inventory?.provider === InventoryType.VAUTO
        ? TradeInType.VAUTO
        : undefined),
  };
};

/**
 * Returns a dealer with defaults based on the set tradeInManagerType
 */
export const addTradeInManagerDefaults = <T extends Partial<Dealer>>(
  dealer: T,
): T => {
  const newDealer = cloneDeep(dealer);
  if (newDealer.tradeInManagerType === TradeInType.VAUTO) {
    // set vauto enabled defaults based on products selected
    if (newDealer.launchpad?.features?.online) {
      set(newDealer, 'vauto.enabled.Online', true);
    }
    if (newDealer.launchpad?.features?.instore) {
      set(newDealer, 'vauto.enabled.In-store', true);
    }
    // default the vauto api username to the web username
    if (newDealer.vauto?.login?.username) {
      set(newDealer, 'vauto.username', newDealer.vauto.login.username);
    }
  } else if (newDealer.tradeInManagerType === TradeInType.FIRSTLOOK) {
    // FirstLook partner id is always 8
    set(newDealer, 'firstlook.partnerId', 8);
  } else if (newDealer.tradeInManagerType === '') {
    // an empty string value is not a valid tradeInManagerType and needs replaced with a value of null
    set(newDealer, 'tradeInManagerType', null);
  }
  return newDealer;
};

/**
 * Returns a dealer with defaults based on the set wizard update emails
 */
export const addOnlineDealNotificationDefaults = <T extends Partial<Dealer>>(
  dealer: T,
): T => {
  return {
    ...dealer,
    wizard: {
      ...dealer.wizard,
      updateEmails: dealer?.wizard?.updateEmails || { enabled: false },
    },
  };
};

export const addDeskingDefaults = <T extends Partial<Dealer>>(
  dealer: T,
): T => ({
  ...dealer,
  desking: {
    enabled: true,
    defaultPencil: false,
    addInceptionFeesToTotalAtStart: true,
    ignoreMRM: false,
    enableOnePayLease: false,
    applyRebatesToDueAtSigning: false,
    ...dealer.desking,
    useTradeInAllowanceForTax: {
      enabled: true,
      [Condition.NEW]: true,
      [Condition.USED]: true,
      ...dealer.desking?.useTradeInAllowanceForTax,
    },
    inceptionFeesTotalAtStart: {
      taxes: { enabled: false },
      fees: { enabled: false },
      firstMonth: { enabled: false },
      customerToggle: { enabled: false },
      enabledByDefault: true,
      ...dealer?.desking?.inceptionFeesTotalAtStart,
    },
  },
});
